import {
  Bell,
  Users2,
  Building2,
  Receipt,
  Megaphone,
  BarChart3,
  ListChecks,
  BellRing,
  LayoutDashboard,
  ScrollText,
  Settings,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

import type { UserRole } from '@/types/app';

/**
 * The single source of truth for which roles see which navigation items (#29).
 *
 * Nothing else in the app decides nav visibility — the sidebar renders whatever
 * navigationFor() returns. Route guards enforce the same lists server-adjacent, so a
 * hidden item is not merely hidden: typing the URL hits RequireRole and, underneath
 * that, RLS.
 *
 * Only destinations that exist today are listed. Leads, tasks, inventory, deals and
 * dashboards arrive with the phases that build them (docs/02-phases.md) — advertising
 * a route that 404s is worse than a short menu.
 */

export interface NavItem {
  /** i18n key suffix: `nav.<key>` */
  key: string;
  to: string;
  icon: LucideIcon;
  roles: readonly UserRole[];
}

const EVERY_ROLE: readonly UserRole[] = [
  'sales',
  'team_leader',
  'sales_manager',
  'marketing',
  'finance',
  'admin',
  'ceo',
];

export const NAVIGATION: readonly NavItem[] = [
  {
    key: 'dashboard',
    to: '/',
    icon: LayoutDashboard,
    roles: EVERY_ROLE,
  },
  {
    key: 'leads',
    to: '/leads',
    icon: Users2,
    // finance works deals and commissions, not customer contact data (§2).
    roles: ['sales', 'team_leader', 'sales_manager', 'marketing', 'admin', 'ceo'],
  },
  {
    key: 'tasks',
    to: '/tasks',
    icon: ListChecks,
    // finance has no lead access, so a task queue would be empty for them.
    roles: ['sales', 'team_leader', 'sales_manager', 'admin'],
  },
  {
    key: 'alerts',
    to: '/alerts',
    icon: BellRing,
    // The oversight view (#36). A rep works their own queue from My tasks.
    roles: ['team_leader', 'sales_manager', 'admin', 'ceo'],
  },
  {
    key: 'inventory',
    to: '/inventory',
    icon: Building2,
    // A price list is not confidential inside the company, and finance prices deals
    // against it — this mirrors the units_select_all policy, which is "any active
    // user". Writing is what is restricted, and that is enforced in the database.
    roles: EVERY_ROLE,
  },
  {
    key: 'reports',
    to: '/reports',
    icon: BarChart3,
    // Every role that holds leads can read reports; the views are
    // security_invoker, so a rep's funnel is their own funnel and a team
    // leader's is their team's. Finance works from the commission page instead.
    roles: ['sales', 'team_leader', 'sales_manager', 'admin', 'ceo', 'marketing'],
  },
  {
    key: 'campaigns',
    to: '/campaigns',
    icon: Megaphone,
    // Mirrors v_campaign_performance, which checks the same roles. Spend is money,
    // and reps have no business with it (#17).
    roles: ['marketing', 'sales_manager', 'admin', 'ceo', 'finance'],
  },
  {
    key: 'commissions',
    to: '/commissions',
    icon: Receipt,
    // Mirrors the SELECT policies on commissions: finance owns the lifecycle,
    // managers and the CEO read it, a team leader sees their team and a rep sees
    // their own rows. Marketing has no business here at all.
    roles: ['finance', 'admin', 'sales_manager', 'ceo', 'team_leader', 'sales'],
  },
  {
    key: 'notifications',
    to: '/notifications',
    icon: Bell,
    roles: EVERY_ROLE,
  },
  {
    key: 'users',
    to: '/admin/users',
    icon: Users,
    roles: ['admin'],
  },
  {
    key: 'teams',
    to: '/admin/teams',
    icon: UsersRound,
    // A sales manager runs the org chart day to day; admin_upsert_team() permits both.
    roles: ['admin', 'sales_manager'],
  },
  {
    key: 'audit',
    to: '/admin/audit',
    icon: ScrollText,
    // Oversight (#31), matching the audit_log_select_oversight RLS policy exactly.
    roles: ['admin', 'ceo', 'sales_manager'],
  },
  {
    key: 'settings',
    to: '/admin/settings',
    icon: Settings,
    roles: ['admin'],
  },
] as const;

export function navigationFor(role: UserRole | null | undefined): NavItem[] {
  if (!role) return [];
  return NAVIGATION.filter((item) => item.roles.includes(role));
}

/** Roles permitted to reach a path, for the route guards. */
export function rolesForPath(path: string): readonly UserRole[] {
  return NAVIGATION.find((item) => item.to === path)?.roles ?? EVERY_ROLE;
}
